import type { InputHTMLAttributes } from 'react';

import { css, styled } from 'styled-components';

import { FormControl, FormControlChildrenCSS } from '../FormControl';

export const lightInput = css`
  background-color: ${(props) => props.theme.colors.slateGray[10]};
  color: ${(props) => props.theme.colors.oxfordBlue[100]};
  border: 2px solid ${(props) => props.theme.colors.slateGray[10]};

  &::placeholder {
    color: ${(props) => props.theme.colors.oxfordBlue[80]};
  }

  &:disabled {
    border: 1px solid ${(props) => props.theme.colors.slateGray[10]};
    &::placeholder {
      color: ${(props) => props.theme.colors.oxfordBlue[30]};
    }
  }
`;

export const darkInput = css`
  background-color: inherit;
  color: ${(props) => props.theme.colors.white};
  border: 2px solid ${(props) => props.theme.colors.oxfordBlue[80]};

  &::placeholder {
    color: ${(props) => props.theme.colors.oxfordBlue[10]};
  }

  &:disabled {
    background-color: inherit;
    border: 1px solid ${(props) => props.theme.colors.oxfordBlue[80]};
    &::placeholder {
      color: ${(props) => props.theme.colors.oxfordBlue[80]};
    }
  }
`;

export const twilightInput = css`
  background-color: ${(props) => props.theme.colors.slateGray[10]};
  color: ${(props) => props.theme.colors.oxfordBlue[100]};
  border: 2px solid ${(props) => props.theme.colors.slateGray[10]};

  &::placeholder {
    color: ${(props) => props.theme.colors.oxfordBlue[80]};
  }

  &:disabled {
    background-color: inherit;
    border: 1px solid ${(props) => props.theme.colors.oxfordBlue[80]};
    &::placeholder {
      color: ${(props) => props.theme.colors.oxfordBlue[30]};
    }
  }
`;

const InputSC = styled.input<{
  $colorVariant?: 'light' | 'dark' | 'twilight';
  $isDisabled?: boolean;
  $isValid?: boolean;
}>`
  ${(props) => {
    switch (props.$colorVariant) {
      case 'dark':
        return darkInput;
      case 'twilight':
        return twilightInput;
      default:
        return lightInput;
    }
  }}

  ${FormControlChildrenCSS}

  height: 3rem;
`;

type InputType = 'email' | 'hidden' | 'number' | 'password' | 'reset' | 'search' | 'submit' | 'tel' | 'text' | 'url';

export type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  colorVariant?: 'light' | 'dark' | 'twilight';
  'data-test-id'?: string;
  hint?: string;
  isDisabled?: boolean;
  isValid?: boolean;
  label?: string;
  type?: InputType;
  validationPassedLabel?: string;
  validationWarningLabel?: string;
};

export const Input = ({
  colorVariant,
  hint,
  isDisabled,
  isValid,
  label,
  type = 'text',
  validationPassedLabel,
  validationWarningLabel,
  ...rest
}: Props) => {
  return (
    <FormControl
      colorVariant={colorVariant}
      hint={hint}
      isDisabled={isDisabled}
      isValid={isValid}
      label={label}
      validationPassedLabel={validationPassedLabel}
      validationWarningLabel={validationWarningLabel}
    >
      <InputSC $isValid={isValid} type={type} {...rest} $colorVariant={colorVariant} disabled={isDisabled} />
    </FormControl>
  );
};
